export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code = 'error',
    readonly fields?: Record<string, string>,
    /** Extra context from the server, e.g. who completed a group task first. */
    readonly details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Thrown when the request never reached the server, the offline case. */
export class NetworkError extends Error {
  constructor(message = 'Keine Verbindung zum Server.') {
    super(message)
    this.name = 'NetworkError'
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
}

async function toApiError(response: Response): Promise<ApiError> {
  let message = `Fehler ${response.status}`
  let code = 'error'
  let fields: Record<string, string> | undefined
  let details: Record<string, unknown> | undefined
  try {
    const data = (await response.json()) as {
      message?: string
      error?: string
      details?: { fields?: Record<string, string> } & Record<string, unknown>
    }
    if (data.message) message = data.message
    if (data.error) code = data.error
    fields = data.details?.fields
    details = data.details
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(response.status, message, code, fields, details)
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch {
    throw new NetworkError()
  }

  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export async function uploadPhoto(
  blob: Blob,
  kind: 'before' | 'after' | 'other' | 'issue' | 'inspection' | 'signature',
  takenAt?: string,
): Promise<{ id: string; thumbUrl: string }> {
  const form = new FormData()
  form.append('file', blob, 'foto.jpg')
  if (takenAt) form.append('takenAt', takenAt)

  let response: Response
  try {
    response = await fetch(`/api/photos?kind=${kind}`, { method: 'POST', credentials: 'same-origin', body: form })
  } catch {
    throw new NetworkError()
  }
  if (!response.ok) throw await toApiError(response)
  return response.json() as Promise<{ id: string; thumbUrl: string }>
}

/** Files a document at a node of the tree. `share` makes it visible in the Kundenportal. */
export async function uploadDocument(objectId: string, file: File, share: boolean): Promise<{ id: string }> {
  const form = new FormData()
  // Before the file: the server reads fields that arrive ahead of it.
  form.append('share', share ? 'true' : 'false')
  form.append('file', file, file.name)
  let response: Response
  try {
    response = await fetch(`/api/objects/${objectId}/documents`, { method: 'POST', credentials: 'same-origin', body: form })
  } catch {
    throw new NetworkError()
  }
  if (!response.ok) throw await toApiError(response)
  return response.json() as Promise<{ id: string }>
}

/**
 * Any import file as CSV text: .csv is read in the browser, .xlsx goes to the
 * server once and comes back as text, so every import takes Excel directly.
 */
export async function readTableFile(file: File): Promise<string> {
  if (/\.(csv|txt)$/i.test(file.name)) return file.text()
  const form = new FormData()
  form.append('file', file, file.name)
  let response: Response
  try {
    response = await fetch('/api/imports/table', { method: 'POST', credentials: 'same-origin', body: form })
  } catch {
    throw new NetworkError()
  }
  if (!response.ok) throw await toApiError(response)
  return ((await response.json()) as { csv: string }).csv
}
